"use client";

import type { ReactNode } from "react";
import type { ChannelDataFreshness } from "@mlx/contracts";

import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { cn } from "@/lib/utils";

type Presentation = {
  label: string;
  summary: string;
  dotClassName: string;
};

export type ChannelDataFreshnessFact =
  | { label: string; timestamp: number | null }
  | { label: string; text: string };

const REASON_LABELS: Record<ChannelDataFreshness["reasonCodes"][number], string> = {
  native_canonical: "Stored directly in MLX",
  native_unpopulated: "No records have been stored yet",
  within_target: "Within the freshness target",
  approaching_target: "Approaching the freshness target",
  ingestion_active: "Source ingestion is running",
  projection_active: "Projection refresh is running",
  source_stale: "Source data is beyond its freshness target",
  source_failed: "The latest source ingestion failed",
  projection_failed: "The latest projection refresh failed",
  projection_behind_source: "The displayed projection is behind the source",
  snapshot_current: "The captured snapshot is current",
  snapshot_expired: "The captured snapshot has expired",
  lineage_unknown: "Source lineage has not been declared",
  query_time_only: "Only the query time is known",
  watermark_unavailable: "The source watermark is not available",
};

export function channelDataFreshnessReason(
  reasonCodes: ChannelDataFreshness["reasonCodes"],
): string {
  return reasonCodes.map((reason) => REASON_LABELS[reason]).join("; ");
}

export function channelDataFreshnessPresentation(
  status: ChannelDataFreshness["status"] | "loading",
  kind?: ChannelDataFreshness["kind"],
): Presentation {
  if (status === "fresh") return {
    label: "Data is fresh",
    summary: "The displayed data is within its freshness target.",
    dotClassName: "bg-emerald-500 ring-emerald-500/20",
  };
  if (status === "aging") return {
    label: "Data is getting older",
    summary: "The data is approaching its freshness limit.",
    dotClassName: "bg-amber-500 ring-amber-500/20",
  };
  if (status === "refreshing") return {
    label: "Data is refreshing",
    summary: "New data is being ingested or applied. Existing data remains available.",
    dotClassName: "bg-amber-500 ring-amber-500/20",
  };
  if (status === "stale") return {
    label: "Data is stale",
    summary: "The displayed data is older than its freshness target.",
    dotClassName: "bg-red-500 ring-red-500/20",
  };
  if (status === "failed") return {
    label: "Refresh needs attention",
    summary: "The latest ingestion or projection refresh failed. Existing data remains available.",
    dotClassName: "bg-red-500 ring-red-500/20",
  };
  if (status === "snapshot") return {
    label: "Snapshot data",
    summary: "This view intentionally shows a captured snapshot.",
    dotClassName: "bg-slate-400 ring-slate-400/20",
  };
  if (status === "unknown" && kind === "query_only") return {
    label: "Source recency unavailable",
    summary: "Only the time this view was queried is known.",
    dotClassName: "bg-slate-400 ring-slate-400/20",
  };
  return {
    label: status === "loading" ? "Checking data freshness" : "Freshness is unknown",
    summary: status === "loading"
      ? "Freshness details are loading."
      : "This data source has not declared an exact freshness contract yet.",
    dotClassName: "bg-slate-400 ring-slate-400/20",
  };
}

export function ChannelDataFreshnessIndicator(props: {
  freshness: ChannelDataFreshness | null;
  loading?: boolean;
}) {
  const status = props.freshness?.status ?? (props.loading ? "loading" : "unknown");
  const presentation = channelDataFreshnessPresentation(
    status,
    props.freshness?.kind,
  );
  return (
    <Popover>
      <PopoverTrigger asChild>
        <button
          type="button"
          className="inline-flex size-7 shrink-0 items-center justify-center rounded-full outline-none hover:bg-muted focus-visible:ring-2 focus-visible:ring-ring/50"
          aria-label={presentation.label}
          title={presentation.label}
        >
          <span
            aria-hidden
            className={cn("size-2.5 rounded-full ring-4", presentation.dotClassName)}
          />
        </button>
      </PopoverTrigger>
      <PopoverContent
        align="end"
        aria-label="Data freshness"
        className="w-[min(24rem,calc(100vw-1rem))] p-0"
      >
        <div className="flex items-center gap-2 px-2 py-2" title={presentation.summary}>
          <span
            aria-hidden
            className={cn("size-2.5 shrink-0 rounded-full ring-4", presentation.dotClassName)}
          />
          <p className="min-w-0 text-sm font-medium text-foreground">{presentation.label}</p>
        </div>
        {props.freshness ? <FreshnessDetails freshness={props.freshness} /> : null}
      </PopoverContent>
    </Popover>
  );
}

export function channelDataFreshnessFacts(
  freshness: ChannelDataFreshness,
): ChannelDataFreshnessFact[] {
  const facts: ChannelDataFreshnessFact[] = [];
  const seen = new Set<number>();
  const addTimestamp = (
    label: string,
    value: number | null,
    options?: { always?: boolean },
  ) => {
    if (value !== null) {
      if (seen.has(value)) return;
      seen.add(value);
    } else if (!options?.always) {
      return;
    } else if (facts.some((fact) => "timestamp" in fact && fact.timestamp === null)) {
      return;
    }
    facts.push({ label, timestamp: value });
  };

  addTimestamp("Data as of", freshness.dataAsOf, { always: true });
  addTimestamp("Last ingestion", freshness.lastIngestionAt, { always: true });
  if (freshness.materialized) {
    addTimestamp("Projection refresh", freshness.lastProjectionRefreshAt, { always: true });
  }
  if (freshness.nextRetryAt !== null) {
    addTimestamp("Next retry", freshness.nextRetryAt);
  } else {
    addTimestamp("Next scheduled", freshness.nextScheduledAt);
  }
  addTimestamp("Snapshot expires", freshness.snapshotExpiresAt);
  facts.push(
    { label: "Why", text: channelDataFreshnessReason(freshness.reasonCodes) },
    {
      label: "Source",
      text: freshness.sourceSystems.map(sourceLabel).join(", ") || "Not declared",
    },
  );
  const sourceReferences = freshness.sourceWatermarks.flatMap((source) =>
    source.sourceReference ? [source.sourceReference] : []
  );
  if (sourceReferences.length > 0) {
    facts.push({ label: "Source reference", text: sourceReferences.join(", ") });
  }
  facts.push({ label: "Checked", timestamp: freshness.generatedAt });
  return facts;
}

function FreshnessDetails(props: { freshness: ChannelDataFreshness }) {
  return (
    <dl className="border-t pb-1 text-xs" aria-label="Freshness details">
      {channelDataFreshnessFacts(props.freshness).map((fact, index) => (
        <FreshnessMetaRow key={fact.label} label={fact.label} index={index}>
          {"text" in fact ? fact.text : <FreshnessTimestamp value={fact.timestamp} />}
        </FreshnessMetaRow>
      ))}
    </dl>
  );
}

function FreshnessMetaRow(props: {
  label: string;
  index: number;
  children: ReactNode;
}) {
  return (
    <div
      className={cn(
        "flex items-baseline justify-between gap-3 px-2 py-1.5",
        props.index % 2 === 0 ? "bg-foreground/[0.04]" : "",
      )}
    >
      <dt className="shrink-0 text-muted-foreground">{props.label}</dt>
      <dd className="min-w-0 text-right tabular-nums text-foreground">{props.children}</dd>
    </div>
  );
}

function FreshnessTimestamp(props: { value: number | null }) {
  if (props.value === null) return "Not available";
  return (
    <time dateTime={new Date(props.value).toISOString()} title={absoluteDateTime(props.value)}>
      {relativeDateTime(props.value)}
    </time>
  );
}

function relativeDateTime(value: number): string {
  const diff = value - Date.now();
  const formatter = new Intl.RelativeTimeFormat("en", { numeric: "auto" });
  const abs = Math.abs(diff);
  const relative = abs < 60 * 60_000
    ? formatter.format(Math.round(diff / 60_000), "minute")
    : abs < 24 * 60 * 60_000
      ? formatter.format(Math.round(diff / (60 * 60_000)), "hour")
      : formatter.format(Math.round(diff / (24 * 60 * 60_000)), "day");
  return relative;
}

function absoluteDateTime(value: number): string {
  return new Intl.DateTimeFormat("en-GB", {
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
    timeZoneName: "short",
  }).format(value);
}

function sourceLabel(source: string): string {
  if (source.toLowerCase() === "mlx") return "MLX";
  return source.replaceAll("_", " ").replace(/\b\w/gu, (letter) => letter.toUpperCase());
}
